<template>
  <div class="h-64 w-full mx-auto" style="position: relative;">
    <chart
      v-if="datacollection"
      ref="chart"
      class="max-w-full h-full"
      :chart-data="datacollection"
      :options="options"
    />
  </div>
</template>

<script>
import Chart from '@/components/Chart.js'
import { mapGetters } from 'vuex'
import { format } from '@/utils.js'
const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0
})

export default {
  name: 'TrendChartDemo',
  components: {
    Chart
  },
  data() {
    return {
      // datacollection: null,
      options: {
        defaultFontFamily: 'Nunito',
        maintainAspectRatio: false,

        scales: {
          xAxes: [
            {
              ticks: {
                fontFamily: 'Nunito'
              },
              gridLines: { display: true }
            }
          ],
          yAxes: [
            {
              id: 'notStacked',
              gridLines: {
                display: true,
                drawTicks: true
              },
              ticks: {
                fontFamily: 'Nunito',
                beginAtZero: true,
                callback: function(value) {
                  return usd.format(value / 1000) + 'k'
                }
              },
              afterDataLimits: axis => {
                let notStacked = axis.chart.scales['notStacked'].max
                let stacked = axis.chart.scales['stacked'].max
                if (notStacked > stacked) {
                  axis.chart.scales['notStacked'].max = notStacked
                } else {
                  axis.chart.scales['notStacked'].max = stacked
                }
              }
            },
            {
              id: 'stacked',
              stacked: true,
              display: false,
              gridLines: {
                display: true,
                drawTicks: true
              },
              ticks: {
                fontFamily: 'Nunito',
                beginAtZero: true,
                callback: function(value) {
                  return usd.format(value / 1000) + 'k'
                }
              },
              afterDataLimits: axis => {
                let notStacked = axis.chart.scales['notStacked'].max
                let stacked = axis.chart.scales['stacked'].max
                if (notStacked > stacked) {
                  axis.chart.scales['stacked'].max = notStacked
                } else {
                  axis.chart.scales['stacked'].max = stacked
                }
              }
            }
          ]
        },
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            fontFamily: 'Nunito',
            fontStyle: 'bold',
            fontSize: 16,
            fontColor: '#2C3648'
          }
        },
        // title: {
        //   display: true,
        //   text: 'Chart.js Line Chart'
        // },
        tooltips: {
          mode: 'index',
          cornerRadius: 10,
          bodySpacing: 6,
          xPadding: 10,
          yPadding: 10,
          titleAlign: 'center',
          titleFontSize: 14,
          backgroundColor: 'rgba(0,0,0,0.75)',
          titleFontFamily: 'Nunito',
          bodyFontFamily: 'Nunito',
          bodyFontSize: 13,
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label + ': '
              switch (tooltipItem.datasetIndex) {
                case 0:
                  label += format(tooltipItem.yLabel)
                  break
                case 1:
                  label +=
                    Number(
                      data.datasets[tooltipItem.datasetIndex].secondary[
                        tooltipItem.index
                      ]
                    ) +
                    '% (' +
                    format(tooltipItem.yLabel) +
                    ')'
                  break
                case 2:
                  label +=
                    Number(
                      data.datasets[tooltipItem.datasetIndex].secondary[
                        tooltipItem.index
                      ]
                    ) +
                    '% (' +
                    format(tooltipItem.yLabel) +
                    ')'
                  break
                case 3:
                  label +=
                    Number(
                      data.datasets[tooltipItem.datasetIndex].secondary[
                        tooltipItem.index
                      ]
                    ) +
                    '% (' +
                    format(tooltipItem.yLabel) +
                    ')'
                  break
                case 4:
                  label +=
                    Number(
                      data.datasets[tooltipItem.datasetIndex].secondary[
                        tooltipItem.index
                      ]
                    ) +
                    '% (' +
                    format(tooltipItem.yLabel) +
                    ')'
                  break
              }
              return label
            }
          }
        },
        hover: {
          mode: 'nearest',
          intersect: true
        },
        elements: {
          point: {
            radius: 0
          }
        }
      }
    }
  },
  computed: {
    ...mapGetters('storeModule/reports', ['reportsOrderedReverse']),
    datacollection() {
      return {
        labels: [
          ...this.reportsOrderedReverse.map(report =>
            this.$luxon(report.date.seconds, {
              input: {
                zone: 'utc',
                format: 'seconds'
              },
              output: 'MM/dd'
            })
          )
        ],
        datasets: [
          {
            label: 'Total Sales',
            borderColor: 'rgba(255, 99, 132, 1)',
            backgroundColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 3,
            fill: false,
            data: [
              ...this.reportsOrderedReverse.map(report =>
                Math.abs(report.sales.total)
              )
            ]
          },
          {
            label: 'Prime Costs',
            borderColor: 'rgba(255, 159, 64, 1)',
            backgroundColor: 'rgba(255, 159, 64, 1)',
            borderWidth: 3,
            fill: false,
            data: [
              ...this.reportsOrderedReverse.map(report =>
                Math.abs(report.primeCost).toFixed(2)
              )
            ],
            secondary: [
              ...this.reportsOrderedReverse.map(report =>
                (Math.abs(report.primeCostPercent) * 100).toFixed(2)
              )
            ]
            // show percent on hover
          },
          {
            label: 'Labor',
            borderColor: 'rgba(54, 162, 235, 1)',
            backgroundColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 2,
            fill: undefined,
            yAxisID: 'stacked',
            data: [
              ...this.reportsOrderedReverse.map(report =>
                Math.abs(report.payroll.total)
              )
            ],
            secondary: [
              ...this.reportsOrderedReverse.map(report =>
                (Math.abs(report.payroll.totalPercent) * 100).toFixed(2)
              )
            ]
            // show % on hover
          },
          {
            label: 'COGS',
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 2,
            fill: undefined,
            yAxisID: 'stacked',
            data: [
              ...this.reportsOrderedReverse.map(report =>
                Math.abs(report.cogs.total)
              )
            ],
            secondary: [
              ...this.reportsOrderedReverse.map(report =>
                (Math.abs(report.cogs.totalPercent) * 100).toFixed(2)
              )
            ]
            // show percent on hover
          },
          {
            label: 'Profit', // gross margin
            borderColor: 'rgba(153, 102, 255, 1)',
            backgroundColor: 'rgba(153, 102, 255, 1)',
            borderWidth: 2,
            fill: undefined,
            yAxisID: 'stacked',
            data: [
              ...this.reportsOrderedReverse.map(report =>
                Math.abs(report.grossMargin)
              )
            ],
            secondary: [
              ...this.reportsOrderedReverse.map(report =>
                (Math.abs(report.grossMarginPercent) * 100).toFixed(2)
              )
            ]
            // show percent on hover
          }
        ]
      }
    }
  }
  // mounted() {
  //   this.options.scales.yAxes[0].afterDataLimits = axis => {
  //     let notStacked = axis.chart.scales['notStacked'].max
  //     let stacked = axis.chart.scales['stacked'].max
  //     if (notStacked > stacked) {
  //       axis.chart.scales['notStacked'].max = notStacked
  //     } else {
  //       axis.chart.scales['notStacked'].max = stacked
  //     }
  //   }
  //   this.options.scales.yAxes[1].afterDataLimits = axis => {
  //     let notStacked = axis.chart.scales['notStacked'].max
  //     let stacked = axis.chart.scales['stacked'].max
  //     if (notStacked > stacked) {
  //       axis.chart.scales['stacked'].max = notStacked
  //     } else {
  //       axis.chart.scales['stacked'].max = stacked
  //     }
  //   }
  // }
}
</script>
